import { capitalize } from 'lodash/fp';

import {
  CheckMetadata,
  createSimpleCheckMetadataArray,
  formatMetadataValue,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { CheckStatus, IdBarcodeInconsistencyDetection } from 'dashboard/models/check';
import { ID_VERSION_MAP } from 'lib/constants/check';

export const computeIdBarcodeInconsistencyDetectionMetadata = (
  check: IdBarcodeInconsistencyDetection,
): CheckMetadata => {
  if (check.status === CheckStatus.Passed) {
    const {
      version,
      'issuer-number': issuerNumber,
      'issuer-code': issuerCode,
      'issuer-name': issuerName,
    } = check.metadata || {};

    return {
      metadata: createSimpleCheckMetadataArray([
        {
          label: 'Issuer Number',
          value: formatMetadataValue(issuerNumber),
        },
        {
          label: 'Issuer Code',
          value: formatMetadataValue(issuerCode),
        },
        {
          label: 'Issuer Name',
          value: formatMetadataValue(issuerName),
        },
        {
          label: 'Version',
          value: version ? ID_VERSION_MAP[version] : '-',
        },
      ]),
      hideIfVerificationPassed: false,
    };
  } else if (check.status === CheckStatus.Failed && check.reasons && check.reasons.length > 0) {
    const failureReason = check.reasons[0];

    if (check.metadata?.['aamva-inconsistency-info'] == null) {
      return {
        metadata: [],
        hideIfVerificationPassed: false,
      };
    }

    switch (failureReason) {
      case 'format_invalid':
      case 'issuer_invalid':
      case 'revision_invalid':
        return {
          metadata: [],
          hideIfVerificationPassed: false,
        };
      case 'required_field_missing': {
        const detectedVersion = check.metadata.version as string;
        const missingFields = check.metadata['aamva-inconsistency-info']['missing-ids'] || [];

        return {
          metadata: createSimpleCheckMetadataArray([
            {
              label: 'Version',
              value: ID_VERSION_MAP[detectedVersion],
            },
            {
              label: 'Missing Fields',
              value: missingFields.join(', '),
            },
          ]),
          hideIfVerificationPassed: false,
        };
      }
      case 'field_invalid': {
        const detectedVersion = check.metadata.version as string;
        const { field, value } = check.metadata['aamva-inconsistency-info'];

        return {
          metadata: createSimpleCheckMetadataArray([
            {
              label: 'Version',
              value: ID_VERSION_MAP[detectedVersion],
            },
            {
              label: capitalize((field as string).replace('_', ' ')),
              value: value,
            },
          ]),
          hideIfVerificationPassed: false,
        };
      }
      case 'issuer_mismatch': {
        const { 'issuer-code': issuerCode, 'issuer-name': issuerName } = check.metadata;
        const { 'address-state': stateName, 'address-state-code': stateCode } =
          check.metadata['aamva-inconsistency-info'];

        return {
          metadata: createSimpleCheckMetadataArray([
            {
              label: 'Expected Issuer',
              value: `${stateCode || '-'}, ${stateName || '-'}`,
            },
            {
              label: 'Detected Issuer',
              value: `${issuerCode || '-'}, ${issuerName || '-'}`,
            },
          ]),
          hideIfVerificationPassed: false,
        };
      }
    }
  }

  return {
    metadata: [],
    hideIfVerificationPassed: false,
  };
};
